/** Bekat Toolkit health summary helpers (ingest slug: bekat-toolkit-summary). */

export const TOOLKIT_CARD_TITLE = 'Toolkit health'

export const TOOLKIT_CARD_DESCRIPTION =
  'Daily rollup from General Windows Health Assessment (Bekat Toolkit). A quick workstation health check — use the Security tab for antivirus, firewall, and encryption.'

export const ASSESSMENT_SCOPE = [
  'Operating system and computer identity',
  'System uptime',
  'Pending reboot',
  'Memory usage',
  'Fixed disk free space (each drive)',
  'Critical services (Event Log, DNS Client, DHCP)',
  'System event log errors (last 24 hours)',
  'Application event log errors (last 24 hours)',
]

export const TOOLKIT_STATUS_LABELS = {
  PASS: 'Healthy',
  WARN: 'Review recommended',
  FAIL: 'Needs attention',
}

export function normaliseToolkitStatus(value) {
  if (value == null || value === '') return null
  const text = String(value).trim().toUpperCase()
  if (text === 'WARNING') return 'WARN'
  if (text === 'PASS' || text === 'WARN' || text === 'FAIL') return text
  return text
}

export function toolkitStatusLabel(value) {
  const status = normaliseToolkitStatus(value)
  if (!status) return 'No data'
  return TOOLKIT_STATUS_LABELS[status] || status
}

export function toolkitStatusVariant(value) {
  const status = normaliseToolkitStatus(value)
  if (status === 'PASS') return 'success'
  if (status === 'WARN') return 'warning'
  if (status === 'FAIL') return 'destructive'
  return 'secondary'
}

export const CHECK_STATUS_LABELS = {
  PASS: 'Pass',
  WARN: 'Warning',
  FAIL: 'Fail',
  INFO: 'Info',
}

export function normaliseCheckStatus(value) {
  if (value == null || value === '') return null
  const text = String(value).trim().toUpperCase()
  if (text === 'WARNING') return 'WARN'
  if (text in CHECK_STATUS_LABELS) return text
  return text
}

export function checkStatusLabel(value) {
  const status = normaliseCheckStatus(value)
  if (!status) return '—'
  return CHECK_STATUS_LABELS[status] || status
}

export function checkStatusVariant(value) {
  const status = normaliseCheckStatus(value)
  if (status === 'PASS') return 'success'
  if (status === 'WARN') return 'warning'
  if (status === 'FAIL') return 'destructive'
  return 'secondary'
}

export function assessmentChecks(summary) {
  const data = summary || {}
  if (Array.isArray(data.checks) && data.checks.length > 0) {
    return data.checks
  }
  return []
}

export function hasFullChecklist(summary) {
  const data = summary || {}
  const checks = assessmentChecks(data)
  const expected = Number(data.checks_run)
  if (!checks.length) return false
  if (!expected || Number.isNaN(expected)) return checks.length > 1
  return checks.length >= expected
}

export function toolkitStatusMeaning(value) {
  const status = normaliseToolkitStatus(value)
  if (status === 'PASS') {
    return 'Common workstation indicators look normal. No warnings or failures were reported.'
  }
  if (status === 'WARN') {
    return 'One or more non-critical conditions were found. Review the finding below — run a full Toolkit script in Atera if you need more detail.'
  }
  if (status === 'FAIL') {
    return 'At least one significant issue was detected. Investigate the finding below before closing the ticket.'
  }
  return null
}

export function formatToolkitScore(score) {
  if (score == null || score === '') return '—'
  const n = Number(score)
  if (Number.isNaN(n)) return String(score)
  return `${n}/100`
}

export function formatSourceCheck(scriptName, scriptVersion) {
  const parts = [scriptName, scriptVersion && `v${scriptVersion}`].filter(Boolean)
  return parts.length ? parts.join(' ') : 'General Windows Health Assessment'
}

/** Prefer ingest snapshot payload (includes per-check rows) over rollup API fields alone. */
export function toolkitSummaryFromRmmData(rmmData) {
  const snapshots = rmmData?.snapshots
  if (!Array.isArray(snapshots)) return null
  const snapshot = snapshots.find((item) => item.script_slug === 'bekat-toolkit-summary')
  if (!snapshot?.payload) return null
  return {
    ...snapshot.payload,
    collected_at: snapshot.collected_at,
  }
}

export function primaryFinding(summary) {
  const data = summary || {}
  const issues = Array.isArray(data.top_issues) ? data.top_issues : []
  const status = normaliseToolkitStatus(data.overall_status)

  if (issues.length > 0) {
    const top = issues[0]
    return {
      title: top.check || 'Check',
      detail: top.detail || null,
      status: top.status || status,
    }
  }

  if (status === 'PASS') {
    return {
      title: 'No issues flagged',
      detail: data.interpretation || 'All assessed workstation indicators passed.',
      status: 'PASS',
    }
  }

  if (data.interpretation) {
    return {
      title: 'Summary',
      detail: data.interpretation,
      status,
    }
  }

  return null
}
